import { CDK_DRAG_CONFIG, CdkDragDrop, DragDropModule } from "@angular/cdk/drag-drop";
import { NgTemplateOutlet } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnInit,
  Output,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Marketplace, SbAsins, StorePage } from "@front/m19-api-client";
import {
  IAlertComponent,
  IButtonDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  IInputGroupDirective,
  IInputGroupInput,
  ILabelComponent,
  ISelectMenuImports,
  ITooltipDirective,
  ModalRef,
  ModalService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ProductThumbnailComponent } from "@m19-board/product-view/product-thumbnail.component";
import { combineLatest, distinctUntilChanged, startWith } from "rxjs";

export interface SbAsinGroup {
  asins: SbAsins[];
}

export type SpotlightPagesValidation = {
  missingTitle?: number; // index
  missingAsin?: number; // index
};

type SpotlightAsinGroup = FormGroup<{
  title: FormControl<string | null>;
  asin: FormControl<string | null>;
  storePage: FormControl<StorePage | null>;
}>;

const DragConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 10000,
};

@Component({
  selector: "app-store-spotlight-pages",
  templateUrl: "./store-spotlight-pages.component.html",
  styleUrls: ["./store-spotlight-pages.component.scss"],
  providers: [{ provide: CDK_DRAG_CONFIG, useValue: DragConfig }],
  imports: [
    IButtonDirective,
    IIconComponent,
    ProductThumbnailComponent,
    ITooltipDirective,
    DragDropModule,
    NgTemplateOutlet,
    TranslocoDirective,
    IInputGroupDirective,
    IInputGroupInput,
    ReactiveFormsModule,
    IInputDirective,
    IFormFieldComponent,
    ILabelComponent,
    ISelectMenuImports,
    FormsModule,
    IAlertComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoreSpotlightPagesComponent implements OnInit {
  private modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);

  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly existingSbAsins = input<SbAsinGroup | undefined>(undefined);
  readonly storePages = input.required<StorePage[]>();

  readonly storePagesWithoutHome = computed(() => this.storePages().filter((p) => p.storeId !== p.storePageId));

  readonly storeName = computed(() => (this.storePages()?.length ? this.storePages()[0].storeName : undefined));

  readonly storePageNameQuery = signal<string>("");

  readonly queryFilteredStorePages = computed(() =>
    this.storePagesWithoutHome().filter(
      (p) =>
        p.storePageName?.toLowerCase().includes(this.storePageNameQuery().toLowerCase()) ||
        p.storeName?.toLowerCase().includes(this.storePageNameQuery().toLowerCase()),
    ),
  );

  @Input() marketplace?: Marketplace;

  @Output() emitSbAsins = new EventEmitter<SbAsins[]>();
  @Output() emitValidation = new EventEmitter<SpotlightPagesValidation>();

  storeAsinModal?: ModalRef<void>;
  asinQuery = signal<string>("");
  readonly focusedAsinControl = signal<SpotlightAsinGroup | undefined>(undefined);

  readonly filteredAsinList = computed(() => {
    const asinList = this.focusedAsinControl()?.controls.storePage.value?.asinList ?? [];
    return asinList.filter((a) => this.passAsinFilter(a, this.asinQuery()));
  });

  readonly focusedStorePageName = computed(
    () => this.focusedAsinControl()?.controls.storePage.value?.storePageName ?? "",
  );

  noAsinsFound = false;

  form = this.fb.group({
    sbAsin1: this.fb.group({
      title: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
      asin: new FormControl<string | null>(null, [Validators.required]),
      storePage: new FormControl<StorePage | null>(null, [Validators.required]),
    }),
    sbAsin2: this.fb.group({
      title: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
      asin: new FormControl<string | null>(null, [Validators.required]),
      storePage: new FormControl<StorePage | null>(null, [Validators.required]),
    }),
    sbAsin3: this.fb.group({
      title: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(50)]),
      asin: new FormControl<string | null>(null, [Validators.required]),
      storePage: new FormControl<StorePage | null>(null, [Validators.required]),
    }),
  });

  asinOrder: FormGroup[] = [this.form.controls.sbAsin1, this.form.controls.sbAsin2, this.form.controls.sbAsin3];

  @ViewChild("asinModalTemplate") asinModalTemplate!: TemplateRef<any>;

  constructor() {
    combineLatest([toObservable(this.existingSbAsins), toObservable(this.storePagesWithoutHome)])
      .pipe(takeUntilDestroyed())
      .subscribe(([existingSbAsins, storePages]) => {
        if (existingSbAsins) {
          this.setupExistingSbAsins(existingSbAsins, storePages);
        } else {
          this.initSbAsins(storePages);
        }
      });
  }

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => {
          return JSON.stringify(a) === JSON.stringify(b);
        }),
        startWith(this.form.value),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((_) => {
        this.emitAsins();
      });
  }

  private setupExistingSbAsins(s: SbAsinGroup, storePages: StorePage[]) {
    const asins = s.asins[0];
    const storePage1 = storePages.find((p) => p.storePageId === asins.storePageId1);
    const storePage2 = storePages.find((p) => p.storePageId === asins.storePageId2);
    const storePage3 = storePages.find((p) => p.storePageId === asins.storePageId3);

    this.form.patchValue({
      sbAsin1: { title: asins.title1!, asin: asins.asin1!, storePage: storePage1! },
      sbAsin2: { title: asins.title2!, asin: asins.asin2!, storePage: storePage2! },
      sbAsin3: { title: asins.title3!, asin: asins.asin3!, storePage: storePage3! },
    });
  }

  private initSbAsins(storePages: StorePage[]) {
    const pages = storePages.filter((p) => p.asinList && p.asinList.length > 0).slice(0, 3);
    if (!pages || pages.length === 0) {
      this.noAsinsFound = true;
      return;
    }
    const asins = pages.map((p) => p.asinList![0]);
    this.form.patchValue({
      sbAsin1: { title: pages[0].storePageName, asin: asins[0], storePage: pages[0] },
      sbAsin2: { title: pages[1].storePageName, asin: asins[1], storePage: pages[1] },
      sbAsin3: { title: pages[2].storePageName, asin: asins[2], storePage: pages[2] },
    });
  }

  drop(event: CdkDragDrop<FormGroup>) {
    const [previousAsin, currentAsin] = [this.asinOrder[event.previousIndex], this.asinOrder[event.currentIndex]];
    const tmp = previousAsin.value;

    previousAsin.setValue(currentAsin.value);
    currentAsin.setValue(tmp);
  }

  private emitAsins() {
    const res: SbAsins[] = [
      {
        title1: this.asinOrder[0].controls["title"].value,
        asin1: this.asinOrder[0].controls["asin"].value,
        storePageId1: this.asinOrder[0].controls["storePage"].value?.storePageId,
        title2: this.asinOrder[1].controls["title"].value,
        asin2: this.asinOrder[1].controls["asin"].value,
        storePageId2: this.asinOrder[1].controls["storePage"].value?.storePageId,
        title3: this.asinOrder[2].controls["title"].value,
        asin3: this.asinOrder[2].controls["asin"].value,
        storePageId3: this.asinOrder[2].controls["storePage"].value?.storePageId,
      },
    ];
    this.emitSbAsins.emit(res);
  }

  changeStorePage(asinControl: FormGroup, page: StorePage) {
    asinControl.controls["storePage"].setValue(page);
    asinControl.controls["asin"].setValue(page.asinList![0]);
    asinControl.controls["title"].setValue(page.storePageName);
  }

  isPageSelected(page: StorePage): boolean {
    return (
      this.form.controls.sbAsin1.controls.storePage.value!.storePageId === page.storePageId ||
      this.form.controls.sbAsin2.controls.storePage.value!.storePageId === page.storePageId ||
      this.form.controls.sbAsin3.controls.storePage.value!.storePageId === page.storePageId
    );
  }

  openAsinList(asinControl: FormGroup) {
    this.asinQuery.set("");
    this.focusedAsinControl.set(asinControl as SpotlightAsinGroup);
    this.storeAsinModal = this.modalService.openModal(this.asinModalTemplate, {
      modalTitle: this.translocoService.translate("store-spotlight-pages.select_asin"),
      height: "fill",
    });
  }

  isAsinSelectedForPage(asin: string) {
    return this.focusedAsinControl()?.controls.asin.value === asin;
  }

  selectAsin(asin: string) {
    this.focusedAsinControl()?.controls.asin.setValue(asin);
  }

  private passAsinFilter(asin: string, query: string) {
    return new RegExp(query, "i").test(asin);
  }
}
